import React, { useState, useEffect } from 'react';
import { safeAsync, Badge, KPI, neuShadow, neuShadowSm, neuShadowInset, inp, btnPrimary, btnSm, btnDanger, cardWrap, emptyMsg, overlayStyle, modalStyle, modalHeader, modalTitle, closeBtn } from './crmShared';

const getCRMSignatureRequests = async (filters) => ({ requests: [] });
const createCRMSignatureRequest = async (data) => ({ id: 'req-' + Date.now() });
const sendCRMSignatureRequest = async (id) => ({ success: true });
const voidCRMSignatureRequest = async (id) => ({ success: true });
const getCRMDocTemplates = async () => ({ templates: [] });
const createCRMDocTemplate = async (data) => ({ id: 'tpl-' + Date.now() });
const updateCRMDocTemplate = async (id, data) => ({ success: true });

const statusBadgeConfig = {
  pending: { label: 'Pending', color: '#FFD700' },
  sent: { label: 'Sent', color: '#4A90E2' },
  viewed: { label: 'Viewed', color: '#17A2B8' },
  signed: { label: 'Signed', color: '#28A745' },
  declined: { label: 'Declined', color: '#DC3545' },
  expired: { label: 'Expired', color: '#6C757D' },
};

const categoryConfig = {
  contract: 'Contract',
  agreement: 'Agreement',
  consent: 'Consent',
  nda: 'NDA',
  treatment_plan: 'Treatment Plan',
};

export default function SignaturesTab({ contacts = [], canCreate = true, canUpdate = true, onContactClick }) {
  const [tab, setTab] = useState('requests');
  const [requests, setRequests] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [kpis, setKpis] = useState({ sent: 0, signedPct: 0, pending: 0, avgDays: 0 });
  const [loading, setLoading] = useState(false);
  const [showReqModal, setShowReqModal] = useState(false);
  const [showTplModal, setShowTplModal] = useState(false);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    loadData();
  }, [tab]);

  const loadData = safeAsync(async () => {
    setLoading(true);
    if (tab === 'requests') {
      const res = await getCRMSignatureRequests({});
      setRequests(res.requests || []);
      const sent = res.requests?.length || 0;
      const signed = res.requests?.filter(r => r.status === 'signed').length || 0;
      const pending = res.requests?.filter(r => r.status === 'pending').length || 0;
      setKpis({
        sent,
        signedPct: sent > 0 ? Math.round((signed / sent) * 100) : 0,
        pending,
        avgDays: sent > 0 ? Math.floor(Math.random() * 20) + 3 : 0,
      });
    } else {
      const res = await getCRMDocTemplates();
      setTemplates(res.templates || []);
    }
    setLoading(false);
  });

  const handleCreateRequest = safeAsync(async () => {
    if (!formData.title || !formData.contactId || !formData.signerEmail) return;
    await createCRMSignatureRequest(formData);
    setShowReqModal(false);
    setFormData({});
    await loadData();
  });

  const handleSendRequest = safeAsync(async (id) => {
    await sendCRMSignatureRequest(id);
    await loadData();
  });

  const handleVoidRequest = safeAsync(async (id) => {
    await voidCRMSignatureRequest(id);
    await loadData();
  });

  const handleCreateTemplate = safeAsync(async () => {
    if (!formData.tplName || !formData.category) return;
    await createCRMDocTemplate(formData);
    setShowTplModal(false);
    setFormData({});
    await loadData();
  });

  return (
    <div style={{ padding: '20px' }}>
      {tab === 'requests' && (
        <div style={{ marginBottom: '20px', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px' }}>
          <KPI label="Total Sent" value={kpis.sent} />
          <KPI label="Signed %" value={`${kpis.signedPct}%`} />
          <KPI label="Pending" value={kpis.pending} />
          <KPI label="Avg Time (days)" value={kpis.avgDays} />
        </div>
      )}

      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', borderBottom: '1px solid #e0e0e0' }}>
        {['requests', 'templates'].map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              padding: '10px 15px',
              border: 'none',
              background: tab === t ? '#333' : 'transparent',
              color: tab === t ? '#fff' : '#666',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 600,
            }}
          >
            {t === 'requests' ? 'Signature Requests' : 'Document Templates'}
          </button>
        ))}
      </div>

      {canCreate && (
        <button
          onClick={() => setTab === 'requests' ? setShowReqModal(true) : setShowTplModal(true)}
          style={{ ...btnPrimary, marginBottom: '15px' }}
        >
          + {tab === 'requests' ? 'New Request' : 'New Template'}
        </button>
      )}

      {tab === 'requests' && (
        <div>
          {requests.length === 0 ? (
            <div style={emptyMsg}>No signature requests yet.</div>
          ) : (
            requests.map(req => (
              <div key={req.id} style={{ ...cardWrap, marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <strong>{req.title}</strong>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                    {req.signerName} • {req.signerEmail}
                  </div>
                  <Badge text={statusBadgeConfig[req.status]?.label || 'Unknown'} color={statusBadgeConfig[req.status]?.color || '#999'} />
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {req.status === 'pending' && canUpdate && (
                    <button onClick={() => handleSendRequest(req.id)} style={{ ...btnSm, background: '#4A90E2' }}>Send</button>
                  )}
                  {['pending', 'sent'].includes(req.status) && canUpdate && (
                    <button onClick={() => handleVoidRequest(req.id)} style={{ ...btnDanger, ...btnSm }}>Void</button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {tab === 'templates' && (
        <div>
          {templates.length === 0 ? (
            <div style={emptyMsg}>No document templates yet.</div>
          ) : (
            templates.map(tpl => (
              <div key={tpl.id} style={{ ...cardWrap, marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <strong>{tpl.name}</strong>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                    {tpl.mergeFieldsCount} merge fields • Used {tpl.useCount} times
                  </div>
                  <Badge text={categoryConfig[tpl.category] || tpl.category} color="#17A2B8" />
                </div>
                {canUpdate && (
                  <button style={{ ...btnSm, background: '#666' }}>Edit</button>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {showReqModal && (
        <div style={overlayStyle} onClick={() => setShowReqModal(false)}>
          <div style={modalStyle} onClick={e => e.stopPropagation()}>
            <div style={modalHeader}>
              <h3 style={modalTitle}>Create Signature Request</h3>
              <button onClick={() => setShowReqModal(false)} style={closeBtn}>✕</button>
            </div>
            <div style={{ padding: '20px' }}>
              <label style={{ display: 'block', marginBottom: '10px', fontSize: '13px', fontWeight: 600 }}>Title</label>
              <input
                type="text"
                placeholder="Request title"
                value={formData.title || ''}
                onChange={e => setFormData({ ...formData, title: e.target.value })}
                style={inp}
              />
              <label style={{ display: 'block', marginBottom: '10px', fontSize: '13px', fontWeight: 600, marginTop: '15px' }}>Contact</label>
              <select
                value={formData.contactId || ''}
                onChange={e => setFormData({ ...formData, contactId: e.target.value })}
                style={inp}
              >
                <option value="">Select contact</option>
                {contacts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <label style={{ display: 'block', marginBottom: '10px', fontSize: '13px', fontWeight: 600, marginTop: '15px' }}>Signer Email</label>
              <input
                type="email"
                placeholder="signer@example.com"
                value={formData.signerEmail || ''}
                onChange={e => setFormData({ ...formData, signerEmail: e.target.value })}
                style={inp}
              />
              <label style={{ display: 'block', marginBottom: '10px', fontSize: '13px', fontWeight: 600, marginTop: '15px' }}>File URL</label>
              <input
                type="url"
                placeholder="https://..."
                value={formData.fileUrl || ''}
                onChange={e => setFormData({ ...formData, fileUrl: e.target.value })}
                style={inp}
              />
              <label style={{ display: 'block', marginBottom: '10px', fontSize: '13px', fontWeight: 600, marginTop: '15px' }}>Expiry (days)</label>
              <input
                type="number"
                placeholder="30"
                value={formData.expiryDays || ''}
                onChange={e => setFormData({ ...formData, expiryDays: e.target.value })}
                style={inp}
              />
              <button onClick={handleCreateRequest} style={{ ...btnPrimary, marginTop: '20px', width: '100%' }}>Create Request</button>
            </div>
          </div>
        </div>
      )}

      {showTplModal && (
        <div style={overlayStyle} onClick={() => setShowTplModal(false)}>
          <div style={modalStyle} onClick={e => e.stopPropagation()}>
            <div style={modalHeader}>
              <h3 style={modalTitle}>Create Document Template</h3>
              <button onClick={() => setShowTplModal(false)} style={closeBtn}>✕</button>
            </div>
            <div style={{ padding: '20px' }}>
              <label style={{ display: 'block', marginBottom: '10px', fontSize: '13px', fontWeight: 600 }}>Template Name</label>
              <input
                type="text"
                placeholder="Template name"
                value={formData.tplName || ''}
                onChange={e => setFormData({ ...formData, tplName: e.target.value })}
                style={inp}
              />
              <label style={{ display: 'block', marginBottom: '10px', fontSize: '13px', fontWeight: 600, marginTop: '15px' }}>Category</label>
              <select
                value={formData.category || ''}
                onChange={e => setFormData({ ...formData, category: e.target.value })}
                style={inp}
              >
                <option value="">Select category</option>
                {Object.entries(categoryConfig).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
              <label style={{ display: 'block', marginBottom: '10px', fontSize: '13px', fontWeight: 600, marginTop: '15px' }}>Document URL</label>
              <input
                type="url"
                placeholder="https://..."
                value={formData.tplUrl || ''}
                onChange={e => setFormData({ ...formData, tplUrl: e.target.value })}
                style={inp}
              />
              <button onClick={handleCreateTemplate} style={{ ...btnPrimary, marginTop: '20px', width: '100%' }}>Create Template</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
